import { Button, Dropdown, Form, FormGroup, Input } from '@/platform/ui';
import { router, useForm, usePage } from '@inertiajs/react';
import DOMPurify from 'dompurify';
import { useState } from 'react';

const TwoFactorForm = () => {
    const {
        auth,
        user,
        two_factor_qr_code: svg,
        password_confirmed: passwordConfirmed,
    } = usePage().props;
    const { data, setData, put, post, errors, processing, reset } = useForm({
        two_factor_type: user?.two_factor_type || 'email',
        code: '',
    });

    const passwordForm = useForm({ password: '' });
    const [pendingAction, setPendingAction] = useState(null);

    const needsConfirmation =
        data.two_factor_type === 'google' && !user?.two_factor_confirmed_at;

    const allowedOptions = [
        {
            id: 'email',
            name: 'Code per e-mail',
        },
        {
            id: 'google',
            name: '(Google) Authenticator (OTP)',
        },
    ].filter(option => auth?.user?.allowed_2fa?.includes(option.id));

    // Changing two-factor settings requires a recent password confirmation; ask for it before the Inertia request
    const withConfirmedPassword = action => {
        if (passwordConfirmed) {
            action();

            return;
        }

        setPendingAction(() => action);
    };

    const handleConfirmPassword = () =>
        passwordForm.post('/user/confirm-password', {
            errorBag: 'confirmPassword',
            preserveScroll: true,
            preserveState: true,
            onSuccess: () => {
                passwordForm.reset();
                setPendingAction(null);
                pendingAction?.();
            },
        });

    const handleTypeChange = value => {
        setData('two_factor_type', value);

        // A pending action belongs to the previous selection
        setPendingAction(null);
        passwordForm.clearErrors();

        if (value === 'google' && !user?.two_factor_confirmed_at) {
            withConfirmedPassword(() =>
                router.post(
                    '/user/two-factor-authentication',
                    {},
                    {
                        preserveScroll: true,
                        preserveState: true,
                    }
                )
            );
        }
    };

    const handleSubmit = () => {
        if (pendingAction) {
            handleConfirmPassword();

            return;
        }

        // Confirm the code before switching the type, so a wrong code never leaves the user on an unconfirmed authenticator
        if (needsConfirmation) {
            withConfirmedPassword(() =>
                post('/user/confirmed-two-factor-authentication', {
                    errorBag: 'confirmTwoFactorAuthentication',
                    preserveScroll: true,
                    preserveState: true,
                    onSuccess: () =>
                        router.put(
                            '/user/two-factor-type',
                            { two_factor_type: 'google' },
                            {
                                preserveScroll: true,
                                preserveState: true,
                                onSuccess: () => reset('code'),
                            }
                        ),
                })
            );

            return;
        }

        const saveType = () =>
            put('/user/two-factor-type', {
                preserveScroll: true,
                preserveState: true,
                onSuccess: () => {
                    if (data.two_factor_type !== 'google') {
                        router.delete('/user/two-factor-authentication', {
                            preserveScroll: true,
                            preserveState: true,
                        });
                    }
                },
            });

        // Confirm the password before saving the type, so the authenticator is always disabled along with it
        if (data.two_factor_type !== 'google') {
            withConfirmedPassword(saveType);

            return;
        }

        saveType();
    };

    return (
        <Form onSubmit={handleSubmit}>
            <FormGroup>
                <Dropdown
                    label='Tweestapsverificatie type'
                    options={allowedOptions}
                    value={data.two_factor_type}
                    onChange={handleTypeChange}
                    error={errors.two_factor_type}
                />

                {pendingAction && (
                    <>
                        <p className='text-sm/6 text-gray-700'>
                            Bevestig je wachtwoord om de instellingen voor
                            tweestapsverificatie te wijzigen.
                        </p>
                        <Input
                            label='Wachtwoord'
                            type='password'
                            autoComplete='current-password'
                            value={passwordForm.data.password}
                            onChange={event =>
                                passwordForm.setData(
                                    'password',
                                    event.target.value
                                )
                            }
                            error={passwordForm.errors.password}
                        />
                    </>
                )}

                {needsConfirmation && !pendingAction && (
                    <>
                        <p className='text-sm/6 text-gray-700'>
                            Scan de volgende QR code met de authenticatie
                            applicatie van je telefoon en voer de gegenereerde
                            OTP code in, om het inschakelen van
                            tweestapsverificatie af te ronden.
                        </p>
                        <div className='flex flex-wrap items-center gap-4'>
                            {svg && (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(svg),
                                    }}
                                />
                            )}
                            <div className='flex flex-col gap-2'>
                                <a
                                    href='https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2'
                                    target='_blank'
                                    rel='noreferrer'
                                >
                                    <img
                                        src='/images/play-store.png'
                                        alt='Android App'
                                        className='h-12'
                                    />
                                </a>
                                <a
                                    href='https://itunes.apple.com/nl/app/google-authenticator/id388497605?mt=8'
                                    target='_blank'
                                    rel='noreferrer'
                                >
                                    <img
                                        src='/images/app-store.png'
                                        alt='iOS App'
                                        className='h-12'
                                    />
                                </a>
                            </div>
                        </div>
                        <Input
                            label='Tweestapsverificatie code'
                            inputMode='numeric'
                            autoComplete='one-time-code'
                            value={data.code}
                            onChange={event =>
                                setData('code', event.target.value)
                            }
                            error={errors.code}
                        />
                    </>
                )}
            </FormGroup>
            <div className='mt-4 flex items-center justify-end'>
                <Button
                    type='submit'
                    variant='primary'
                    loading={processing || passwordForm.processing}
                >
                    {pendingAction
                        ? 'Wachtwoord bevestigen'
                        : needsConfirmation
                          ? 'Bevestig'
                          : 'Opslaan'}
                </Button>
            </div>
        </Form>
    );
};

export default TwoFactorForm;
